import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styled, { useTheme } from 'styled-components';

// Store
import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';
import {
  ClearNotifications,
  ListNotifications,
  MarkAsRead,
} from '@webapp/store/notifications/action-creators';

// Types
import NotificationItem from '@common/types/notifications/notification-item';

// Components
import NewMenu from '@components/new-menu';
import Typography from '@components/basics/typography';
import Flex from '@components/layout/flex';
import { Link } from './avatar-header';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';
import NotificationsWrapperCtrl from '@webapp/controllers/notifications/notifications-wrapper';

interface NotificationsMenuProps {
  className?: string;
  noButton?: boolean;
  avatar?: string;
  fullName: string;
  notifications: NotificationItem[];
  getNotifications: () => ListNotifications;
  clearNotification: (id?: string) => ClearNotifications;
  markAsRead: () => MarkAsRead;
  isMobile: boolean;
}

const mapStateToProps = (state: AppState) => ({
  notifications: state.notifications.items,
  avatar: state.artist.active?.avatar,
  fullName: state.artist.active?.name || '',
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  clearNotification: (id?: string) => dispatch(new ClearNotifications(id)),
  getNotifications: () => dispatch(new ListNotifications()),
  markAsRead: () => dispatch(new MarkAsRead()),
});

const NotificationsMenu: FunctionComponent<NotificationsMenuProps> = ({
  className,
  noButton,
  notifications,
  markAsRead,
  clearNotification,
  avatar,
  fullName,
  isMobile,
}) => {
  const theme = useTheme();

  const numberOfNotifications = notifications.filter(
    (n) => n.status === 'unread'
  ).length;

  React.useEffect(() => {
    if (notifications.some((n) => n.status === 'unread')) {
      const timer = setTimeout(() => markAsRead(), 2000);

      return () => clearTimeout(timer);
    }
  }, [notifications, markAsRead]);

  return (
    <NewMenu
      className={className}
      button={noButton}
      noButton={noButton}
      menuId="NOTIFICATIONS_MENU"
      topHeader={isMobile && <UserAvatarInfo src={avatar} name={fullName} />}
      header={!isMobile && <UserAvatarInfo src={avatar} name={fullName} />}
      footer={null}
    >
      <section className={className}>
        <div className="container">
          <Flex
            className="mb-30"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography type="subheader" customFont="semi-bold">
              <Translate id="NOTIFICATIONS.TITLE" /> ({numberOfNotifications})
            </Typography>
            <Link weight="700" onClick={() => clearNotification()}>
              <Translate id="NOTIFICATIONS.CLEAR" />
            </Link>
          </Flex>
          {notifications.length === 0 && (
            <NoNotificationsText align="center" color={theme.colors.tertiary}>
              <Translate id="NOTIFICATIONS.NO_NOTIFICATIONS" />
            </NoNotificationsText>
          )}
          <NotificationsWrapperCtrl
            notifications={notifications}
            onClear={(id) => clearNotification(id)}
          />
        </div>
      </section>
    </NewMenu>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsMenu);

const NoNotificationsText = styled(Typography)`
  margin-top: 30px;
  @media ${({ theme }) => theme.device.tablet} {
    margin-top: 80px;
  }
`;
